/**
 * Prefill for the "Hạn thanh toán" section in the chốt-sổ confirm dialog, and
 * the collapsed-vs-pre-expanded state that section opens in (design spec:
 * docs/superpowers/specs/2026-08-19-due-date-control-design.md §2). Pure, no
 * clock/network reads — the caller passes `today` explicitly (normally
 * `useServerNow()`, whose UTC getters already read as the VN wall-clock
 * calendar date/time — see sessionTime.ts's `currentMonth`/`prevMonth`,
 * which read `today` the same way).
 *
 * `day` is always 1-28 — `OrganizationSettings.invoiceDueDay`'s Zod cap
 * (`@repo/validation`'s `updateOrgSettingsSchema`) — so every calendar month,
 * including February, has that day-of-month, and there is no month-end
 * overflow to guard against.
 */

function formatDateKey(year: number, monthIndex0: number, day: number): string {
  return `${year}-${String(monthIndex0 + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The soonest occurrence of `day` (day-of-month, 1-28) that is
 * today-or-future, as a `YYYY-MM-DD` string. Today itself counts as future
 * (inclusive boundary) — this MUST agree with the backend's own boundary
 * (`parseFutureDueDate` in apps/api/src/lib/dueDateOverride.ts, which accepts
 * a `dueDate` of exactly today), or a value this lib prefills could be
 * rejected by the API that receives it back.
 */
export function nextDueDateForDay(day: number, today: Date): string {
  const year = today.getUTCFullYear();
  const monthIndex0 = today.getUTCMonth();
  const todayDay = today.getUTCDate();

  // day-of-month not yet passed this month (or exactly today) — this month's
  // occurrence is today-or-future.
  if (day >= todayDay) {
    return formatDateKey(year, monthIndex0, day);
  }

  // Already passed this month — roll to the same day next month, wrapping
  // the year at December → January.
  const nextMonthIndex0 = monthIndex0 === 11 ? 0 : monthIndex0 + 1;
  const nextYear = monthIndex0 === 11 ? year + 1 : year;
  return formatDateKey(nextYear, nextMonthIndex0, day);
}
